/*
  wishlist-works-rows.js — お気に入り作品の「作品別 新着商品ブロック」共通レンダラー
  2026-08 施策①: TOPの棚とお気に入りページで同じ見せ方（左=作品ロゴ / 右=新着商品 / もっと見る）を使う

  使い方:
    WishlistWorksRows.mount(rootElement, {
      token:        Storefront APIトークン（必須）
      maxWorks:     表示する作品数の上限（既定 3）
      maxWorkIds:   読みに行く作品数の上限（既定 20）。お気に入りページは棚と対にするため全作品ぶんを指定する
      perWork:      1作品あたりの商品数（既定 20・新着商品棚と同じ Flickity 横スライドで表示）
      refBlock:     経由売上の目印になる枠名（attribution-ref.js が拾う）
      shelf:        GA4 wishlist_shelf_click の shelf 値
      moreLabel:    もっと見るの文言
      showEmptyWorks: true にすると、今買える商品が無い作品も「販売中の商品はありません」のブロックとして出す
                    （お気に入りページ用。棚のロゴと下のブロックを必ず対にするため。既定 false＝TOPは従来どおり出さない）
      gridClasses:  商品カードに付けるテーマのグリッドclass
      onEmpty/onReady: 呼び出し側で表示切替をするためのコールバック
    });

  仕様メモ:
    - 種は localStorage 'wishlist_work_ids'（作品お気に入り＝コレクションの数値ID）
    - 販売中かつ販売期間内（JST）の商品のみ。publishedAt降順＝新着順
    - 1回目は作品ごとに新しい60商品を見る。買える商品が perWork に満たず、まだ続きがある作品だけ、続きのページを取りに行く
      （新しい60点が全部販売終了でも、古い商品がまだ買える作品がある。例: ラブライブは買える83点が76番目以降）
    - 商品カードは /products/<handle>?view=card-item を fetch してテーマの product-item を再利用
    - カード取得は行ごとの遅延ロード（IntersectionObserver）。作品が多くても初回リクエストを増やさない
    - 計測が失敗しても表示を止めないよう、計測まわりは全て try/catch
*/
(function () {
  var DEFAULTS = {
    endpoint: '/api/2024-07/graphql.json',
    maxWorks: 3,
    perWork: 20,
    fetchPerWork: 60, // 期間外/売切れを除外した結果 perWork 件を確保するための取得数（1回目）
    morePageSize: 100, // 足りない作品だけ追加で取りに行くときの1ページの件数
    maxMorePages: 3,   // 追加で取りに行く回数の上限（作品ごと）
    showEmptyWorks: false,
    emptyText: '現在販売中の商品はありません。新しい商品が出たら、ここに並びます。',
    emptyMoreLabel: '作品ページを見る',
    maxWorkIds: 20,    // 読みに行く作品数の上限（お気に入り登録の古い順）
    idsPerRequest: 20, // 1回の問い合わせで読む作品数。maxWorkIds がこれを超える分は分けて取る
    refBlock: 'wishlist_works_shelf',
    shelf: 'wishlist_works_new',
    moreLabel: 'もっと見る',
    gridClasses: '', // カード幅は wishlist-works-rows.css で固定（全ブロック統一）。矢印だけ新着棚と同じFlickity
    timeoutMs: 8000
  };

  var QUERY = [
    'query WorkRows($ids:[ID!]!, $per:Int!) {',
    '  nodes(ids:$ids) {',
    '    ... on Collection {',
    '      id title handle',
    '      image { url }',
    '      logo: metafield(namespace:"custom", key:"logo") {',
    '        reference { ... on MediaImage { image { url } } }',
    '      }',
    '      products(first:$per, sortKey:CREATED, reverse:true) {',
    '        pageInfo { hasNextPage endCursor }',
    '        edges { node {',
    '          id handle title publishedAt availableForSale',
    '          release: metafield(namespace:"custom", key:"release_date") { value }',
    '          salesEnd: metafield(namespace:"custom", key:"sales_end_date") { value }',
    '        } }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  // 1作品ぶんの続きのページ（買える商品が足りない作品だけに使う）
  var MORE_QUERY = [
    'query WorkRowsMore($id:ID!, $per:Int!, $after:String) {',
    '  node(id:$id) {',
    '    ... on Collection {',
    '      products(first:$per, after:$after, sortKey:CREATED, reverse:true) {',
    '        pageInfo { hasNextPage endCursor }',
    '        edges { node {',
    '          id handle title publishedAt availableForSale',
    '          release: metafield(namespace:"custom", key:"release_date") { value }',
    '          salesEnd: metafield(namespace:"custom", key:"sales_end_date") { value }',
    '        } }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function readWorkIds() {
    try {
      var parsed = JSON.parse(localStorage.getItem('wishlist_work_ids') || '[]');
      if (Array.isArray(parsed)) {
        return Array.from(new Set(parsed.map(String).filter(Boolean)));
      }
    } catch (e) {}
    return [];
  }

  // --- JSTの日付境界（recommended-by-vendor / 旧棚と同一ロジック） ---
  function jstBoundaryMs(val, endOfDay) {
    if (!val) return null;
    try {
      var parts = new Intl.DateTimeFormat('ja-JP', {
        timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(new Date(val));
      var y = parts.find(function (p) { return p.type === 'year'; }).value;
      var m = parts.find(function (p) { return p.type === 'month'; }).value;
      var d = parts.find(function (p) { return p.type === 'day'; }).value;
      return new Date(y + '-' + m + '-' + d + 'T' + (endOfDay ? '23:59:59' : '00:00:00') + '+09:00').getTime();
    } catch (e) { return null; }
  }

  function isSellableNow(p, nowMs) {
    if (!p || !p.handle || !p.availableForSale) return false;
    var start = p.release && p.release.value ? jstBoundaryMs(p.release.value, false) : null;
    var end = p.salesEnd && p.salesEnd.value ? jstBoundaryMs(p.salesEnd.value, true) : null;
    if (start && nowMs < start) return false;
    if (end && nowMs > end) return false;
    return true;
  }

  function fetchWithTimeout(url, opt, ms) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, ms);
    var merged = Object.assign({}, opt || {}, { signal: controller.signal });
    return fetch(url, merged).finally(function () { clearTimeout(timer); });
  }

  function logoUrlOf(c) {
    var fromMeta = c.logo && c.logo.reference && c.logo.reference.image ? c.logo.reference.image.url : '';
    var url = fromMeta || (c.image && c.image.url) || '';
    if (!url) return '';
    return url + (url.indexOf('?') !== -1 ? '&' : '?') + 'width=480';
  }

  function buildRow(collection, products, opt) {
    var handle = collection.handle || '';
    var href = '/collections/' + encodeURIComponent(handle);
    var logo = logoUrlOf(collection);

    var row = document.createElement('div');
    row.className = 'wwr__row' + (logo ? ' wwr__row--has-logo' : '');
    row.setAttribute('data-work-handle', handle);

    var skeleton = products.map(function () { return '<span></span>'; }).join('');
    var isEmpty = !products.length;
    if (isEmpty) row.className += ' wwr__row--empty';

    row.innerHTML =
      '<div class="wwr__head">' +
        (logo
          ? '<a class="wwr__logo-link" href="' + esc(href) + '">' +
              '<img class="wwr__logo" src="' + esc(logo) + '" alt="' + esc(collection.title) + '" loading="lazy">' +
            '</a>'
          : '') +
        '<a class="wwr__name" href="' + esc(href) + '">' + esc(collection.title) + '</a>' +
      '</div>' +
      '<div class="wwr__body">' +
        (isEmpty
          ? '<p class="wwr__none">' + esc(opt.emptyText) + '</p>'
          : '<div class="scroller"><div class="scroller__inner">' +
              '<div class="product-list product-list--vertical product-list--scrollable wwr__list"></div>' +
            '</div></div>' +
            '<div class="wwr__skeleton">' + skeleton + '</div>') +
        '<div class="wwr__foot">' +
          '<a class="wwr__more" href="' + esc(href) + '">' + esc(isEmpty ? opt.emptyMoreLabel : opt.moreLabel) + ' ›</a>' +
        '</div>' +
      '</div>';

    return row;
  }

  // カードHTMLを取得して list に追加（並び順を保つため一括で待ってから追加）
  function appendCards(list, products, opt) {
    var reqs = products.map(function (p) {
      return fetchWithTimeout('/products/' + p.handle + '?view=card-item', { credentials: 'same-origin' }, opt.timeoutMs)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    });
    return Promise.all(reqs).then(function (htmls) {
      var frag = document.createDocumentFragment();
      htmls.filter(Boolean).forEach(function (html) {
        var box = document.createElement('div');
        box.innerHTML = html;
        var item = box.querySelector('.product-item');
        if (item && opt.gridClasses) {
          opt.gridClasses.split(/\s+/).forEach(function (c) { if (c) item.classList.add(c); });
        }
        Array.prototype.slice.call(box.childNodes).forEach(function (n) {
          if (n.nodeType === 1) frag.appendChild(n);
        });
      });
      var added = frag.childNodes.length;
      list.appendChild(frag);
      return added;
    });
  }

  // 新着商品棚(featured-collection)と同じ Flickity 設定（theme.js の FeaturedCollectionSection と同一）
  function flickityOptions() {
    return {
      watchCSS: true,
      pageDots: false,
      prevNextButtons: true,
      contain: true,
      resize: false,
      groupCells: true,     // 表示中のカードをまとめて1ページとして送る（PC=4点/SP=2点。幅はCSSで決まる）
      cellAlign: 'left',
      draggable: !window.matchMedia('(-moz-touch-enabled: 0), (hover: hover)').matches
    };
  }

  // 1行の描画: 最初の FIRST_BATCH 点を先に出して Flickity 化、残りは矢印/スクロール操作時に append
  // （20点×5作品の初期負荷を抑える。Flickity は theme.js が window.Flickity として公開）
  var FIRST_BATCH = 8;
  function fillRow(row, products, opt) {
    var list = row.querySelector('.wwr__list');
    var skeleton = row.querySelector('.wwr__skeleton');
    if (!list) return Promise.resolve();

    var head = products.slice(0, FIRST_BATCH);
    var tail = products.slice(FIRST_BATCH);

    return appendCards(list, head, opt).then(function (added) {
      if (skeleton) skeleton.remove();
      if (!added) { row.remove(); return; }

      // theme.js（defer）が window.Flickity を公開するのを待って初期化する
      // （このJSは theme.js より先に評価されるが、ここは fetch 後なので通常は間に合う。念のため待機）
      function initFlickity() {
        var Flickity = window.Flickity;
        if (typeof Flickity !== 'function') return null;
        try {
          var f = new Flickity(list, flickityOptions());
          if (window.ResizeObserver) {
            var ro = new ResizeObserver(function () { try { f.resize(); } catch (e) {} });
            list.querySelectorAll('.product-item').forEach(function (it) { ro.observe(it); });
            row.__wwrRO = ro;
          }
          return f;
        } catch (e) { return null; }
      }
      var flkty = initFlickity();
      if (!flkty && document.readyState !== 'complete') {
        window.addEventListener('load', function () {
          if (!row.__wwrFlickity) {
            var f2 = initFlickity();
            if (f2) { row.__wwrFlickity = f2; flkty = f2; f2.on('change', loadTail); f2.on('dragStart', loadTail); }
          }
        }, { once: true });
      }
      row.__wwrFlickity = flkty;
      if (!tail.length) return;

      var loadingTail = false;
      function loadTail() {
        if (loadingTail) return;
        loadingTail = true;
        // 残りのカードは一旦オフDOMで作って Flickity.append で追加（並び順維持）
        var tmp = document.createElement('div');
        appendCards(tmp, tail, opt).then(function () {
          var nodes = Array.prototype.slice.call(tmp.childNodes).filter(function (n) { return n.nodeType === 1; });
          if (!nodes.length) return;
          if (flkty) {
            flkty.append(nodes);
            if (row.__wwrRO) nodes.forEach(function (n) { row.__wwrRO.observe(n); });
            try { flkty.resize(); } catch (e) {}
          } else {
            nodes.forEach(function (n) { list.appendChild(n); });
          }
        });
      }
      if (flkty) {
        // 矢印クリック/ドラッグで動いたら残りを読む
        flkty.on('change', loadTail);
        flkty.on('dragStart', loadTail);
      }
      // ホバー/タッチした時点でも先読み（スクロール前の待ちを減らす）
      row.addEventListener('pointerenter', loadTail, { once: true });
      row.addEventListener('touchstart', loadTail, { once: true, passive: true });
    });
  }

  function mount(root, options) {
    if (!root) return;
    var opt = Object.assign({}, DEFAULTS, options || {});
    if (!opt.token) return;

    root.classList.add('wwr');
    root.setAttribute('data-ref-block', opt.refBlock);

    var loading = false;
    var observer = null;

    function done(hasRows) {
      if (hasRows) { if (typeof opt.onReady === 'function') opt.onReady(); }
      else if (typeof opt.onEmpty === 'function') opt.onEmpty();
    }

    function load() {
      if (loading) return;
      var ids = readWorkIds();
      if (!ids.length) { root.innerHTML = ''; done(false); return; }
      loading = true;

      var gids = ids.slice(0, opt.maxWorkIds).map(function (id) {
        return 'gid://shopify/Collection/' + id;
      });

      // 1回の問い合わせは idsPerRequest 作品まで（1回の重さは従来と同じ）。それを超える分は分けて同時に取る
      var chunks = [];
      for (var k = 0; k < gids.length; k += opt.idsPerRequest) chunks.push(gids.slice(k, k + opt.idsPerRequest));

      Promise.all(chunks.map(function (part) {
        return fetchWithTimeout(opt.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': opt.token
          },
          body: JSON.stringify({ query: QUERY, variables: { ids: part, per: opt.fetchPerWork } }),
          credentials: 'same-origin'
        }, opt.timeoutMs)
          .then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          })
          .then(function (json) {
            if (json.errors) { try { console.error('[WWR] gql', json.errors); } catch (e) {} }
            return ((json.data && json.data.nodes) || []).filter(Boolean);
          })
          .catch(function (e) {
            try { console.error('[WWR] load failed', e); } catch (_) {}
            return []; // 一部の問い合わせが失敗しても、取れた作品は出す
          });
      }))
        .then(function (lists) {
          var nodes = [].concat.apply([], lists);
          var nowMs = Date.now();

          // お気に入り登録の並び順（localStorageの並び）を尊重して作品を並べる
          var order = {};
          ids.forEach(function (id, i) { order[id] = i; });
          nodes.sort(function (a, b) {
            var ai = order[String(a.id).split('/').pop()];
            var bi = order[String(b.id).split('/').pop()];
            return (ai == null ? 999 : ai) - (bi == null ? 999 : bi);
          });

          // 作品ごとに「今買える商品」を集める。足りず、まだ続きがある作品だけ続きのページを取りに行く
          function collect(c) {
            var seen = {};
            var items = [];
            function take(conn) {
              ((conn && conn.edges) || []).forEach(function (e) {
                var p = e && e.node;
                if (!p || !isSellableNow(p, nowMs)) return;
                if (seen[p.handle]) return;
                seen[p.handle] = true;
                if (items.length < opt.perWork) items.push(p);
              });
              return conn && conn.pageInfo;
            }
            var page = take(c.products);
            var tries = 0;
            function next() {
              if (items.length >= opt.perWork || !page || !page.hasNextPage || tries >= opt.maxMorePages) {
                return Promise.resolve({ collection: c, products: items });
              }
              tries++;
              return fetchWithTimeout(opt.endpoint, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Shopify-Storefront-Access-Token': opt.token
                },
                body: JSON.stringify({ query: MORE_QUERY, variables: { id: c.id, per: opt.morePageSize, after: page.endCursor } }),
                credentials: 'same-origin'
              }, opt.timeoutMs)
                .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
                .then(function (j) {
                  page = take(j && j.data && j.data.node && j.data.node.products);
                  return next();
                })
                .catch(function () { return { collection: c, products: items }; }); // 続きが取れなくても、集まった分で出す
            }
            return next();
          }

          return Promise.all(nodes.map(collect));
        })
        .then(function (all) {
          var rows = [];
          all.forEach(function (r) {
            if (rows.length >= opt.maxWorks) return;
            if (!r.products.length && !opt.showEmptyWorks) return;
            rows.push(r);
          });

          // 前回描画分の Flickity を破棄
          Array.prototype.forEach.call(root.querySelectorAll('.wwr__row'), function (r) {
            try { if (r.__wwrFlickity) r.__wwrFlickity.destroy(); } catch (e) {}
            try { if (r.__wwrRO) r.__wwrRO.disconnect(); } catch (e) {}
          });
          root.innerHTML = '';
          if (!rows.length) { done(false); return; }

          if (observer) { observer.disconnect(); observer = null; }
          var supportsIO = typeof IntersectionObserver === 'function';
          if (supportsIO) {
            observer = new IntersectionObserver(function (entries, obs) {
              entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;
                obs.unobserve(entry.target);
                var data = entry.target.__wwrData;
                if (data) fillRow(entry.target, data.products, opt);
              });
            }, { rootMargin: '400px 0px' });
          }

          rows.forEach(function (r, i) {
            var el = buildRow(r.collection, r.products, opt);
            el.__wwrData = r;
            root.appendChild(el);
            if (!r.products.length) return; // 販売中の商品が無い作品は案内だけ（読み込む商品が無い）
            // 最初の1行は即描画（ファーストビューの空白を避ける）、残りは近づいたら描画
            if (i === 0 || !supportsIO) fillRow(el, r.products, opt);
            else observer.observe(el);
          });

          done(true);
        })
        .catch(function (e) {
          try { console.error('[WWR] load failed', e); } catch (_) {}
          root.innerHTML = '';
          done(false);
        })
        .finally(function () { loading = false; });
    }

    // 効果測定: 棚経由の商品クリック（GA4）
    root.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
      if (!a) return;
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'wishlist_shelf_click',
          shelf: opt.shelf,
          to_product_url: a.getAttribute('href')
        });
      } catch (_) {}
    });

    // ハートの付け外しに追従
    document.addEventListener('wishlist-works:change', function () { load(); });

    load();
    return { reload: load };
  }

  window.WishlistWorksRows = { mount: mount };
})();
